import { random } from 'lodash-es';

/**
 * Red Light, Green Light Game Configuration
 *
 * This file contains all game constants, difficulty presets, and mock ranking data
 * for the Red Light, Green Light game.
 */

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface Position {
  x: number;
  y: number;
}

export interface PlayerState {
  position: Position;
  isWalking: boolean;
  walkFrame: number;
  isCaught: boolean;
}

export type WatcherState = 'green' | 'peeking' | 'sleeping' | 'turning' | 'red';

export type GameResult = 'win' | 'caught' | 'timeout' | null;

export interface GameConfig {
  TOTAL_TIME: number;
  TIME_MULTIPLIER: number;
  TIME_BONUS_MULTIPLIER: number;
  GREEN_LIGHT_MIN: number;
  GREEN_LIGHT_MAX: number;
  RED_LIGHT_MIN: number;
  RED_LIGHT_MAX: number;
  TURN_ANIMATION_DURATION: number;
  GRACE_PERIOD: number;
  MOVEMENT_SPEED: number;
  WALK_ANIMATION_SPEED: number;
  BOARD_WIDTH: number;
  BOARD_HEIGHT: number;
  PLAYER_START_X: number;
  PLAYER_START_Y: number;
  WATCHER_X: number;
  WATCHER_Y: number;
  DOOR_X: number;
  DOOR_Y: number;
  DOOR_THRESHOLD: number;
  BASE_SCORE: number;
  SPEED_BONUS_MULTIPLIER: number;
}

export interface RankingEntry {
  rank: number;
  nickname: string;
  comment: string;
  score: number;
}

export interface RankingData {
  daily: RankingEntry[];
  weekly: RankingEntry[];
  monthly: RankingEntry[];
}

export type DifficultyLevel = 'EASY' | 'NORMAL' | 'HARD';

// ============================================================================
// Asset Configuration
// ============================================================================

const CDN_BASE = `${process.env.NEXT_PUBLIC_CDN_URL}/public/games`;

export const ASSETS = {
  player: {
    idle: `${CDN_BASE}/m_s_01.png`,
    walk1: `${CDN_BASE}/m_s_01.png`,
    walk2: `${CDN_BASE}/m_s_02.png`,
    caught: `${CDN_BASE}/m_s_01.png`,
  },
  watcher: {
    green: `${CDN_BASE}/m_h_back.png`,
    peeking: `${CDN_BASE}/m_h_side_default.png`,
    sleeping: `${CDN_BASE}/m_h_side_sleep.png`,
    turning: `${CDN_BASE}/m_h_front_01.png`,
    red: `${CDN_BASE}/m_h_front_02.png`,
  },
  environment: {
    background: `${CDN_BASE}/m_background_03.png`,
    door: `${CDN_BASE}/door.png`,
  },
  decorations: [
    `${CDN_BASE}/m_a_01.png`,
    `${CDN_BASE}/m_a_02.png`,
    `${CDN_BASE}/m_a_03.png`,
    `${CDN_BASE}/m_a_04.png`,
  ],
  guide: {
    background: `${CDN_BASE}/m_guide.png`,
  },
};

// ============================================================================
// Game Configuration (Default: NORMAL difficulty)
// ============================================================================

export const RED_LIGHT_GREEN_LIGHT_CONFIG: GameConfig = {
  // Time Settings
  TOTAL_TIME: 60, // Total game duration in seconds
  TIME_MULTIPLIER: 10, // Points per remaining second (linear bonus)
  TIME_BONUS_MULTIPLIER: 2100, // Quadratic time bonus multiplier (dominant)

  // Watcher Behavior
  GREEN_LIGHT_MIN: 2000, // Min duration facing board (ms)
  GREEN_LIGHT_MAX: 5000, // Max duration facing board (ms)
  RED_LIGHT_MIN: 1500, // Min duration facing players (ms)
  RED_LIGHT_MAX: 4000, // Max duration facing players (ms)
  TURN_ANIMATION_DURATION: 500, // Rotation animation time (ms)
  GRACE_PERIOD: 200, // Detection grace period after turn (ms)

  // Player Movement
  MOVEMENT_SPEED: 5, // Pixels per step
  WALK_ANIMATION_SPEED: 150, // Frame switch interval (ms)

  // Game Board (2.5D Perspective Coordinates)
  BOARD_WIDTH: 800, // Game area width (px)
  BOARD_HEIGHT: 600, // Game area height (px)
  PLAYER_START_X: 50, // Starting X position (left side)
  PLAYER_START_Y: 450, // Starting Y position (foreground/bottom)
  WATCHER_X: 400, // Watcher X position (center)
  WATCHER_Y: 280, // Watcher Y position (background/top)
  DOOR_X: 800, // Door X position (right side, 20px from edge)
  DOOR_Y: 450, // Door Y position (same as player, foreground)
  DOOR_THRESHOLD: 30, // Distance to trigger victory (px)

  // Scoring
  BASE_SCORE: 300, // Base completion score (low to emphasize time bonus)
  SPEED_BONUS_MULTIPLIER: 10, // Multiplier for remaining time
};

// ============================================================================
// Difficulty Presets
// ============================================================================

export const DIFFICULTY_PRESETS: Record<DifficultyLevel, Partial<GameConfig>> = {
  EASY: {
    GREEN_LIGHT_MIN: 3000,
    GREEN_LIGHT_MAX: 6000,
    RED_LIGHT_MIN: 1000,
    RED_LIGHT_MAX: 3000,
    GRACE_PERIOD: 300,
  },
  NORMAL: {
    GREEN_LIGHT_MIN: 2000,
    GREEN_LIGHT_MAX: 5000,
    RED_LIGHT_MIN: 1500,
    RED_LIGHT_MAX: 4000,
    GRACE_PERIOD: 200,
  },
  HARD: {
    GREEN_LIGHT_MIN: 1000,
    GREEN_LIGHT_MAX: 3000,
    RED_LIGHT_MIN: 2000,
    RED_LIGHT_MAX: 5000,
    GRACE_PERIOD: 100,
  },
};

// ============================================================================
// Mock Ranking Data
// ============================================================================

export const MOCK_RANKING_DATA: RankingData = {
  daily: [
    { rank: 1, nickname: '빛의속도', comment: '너무 쉬웠어요!', score: 1500 },
    { rank: 2, nickname: '무궁화왕', comment: '재밌네요 ㅎㅎ', score: 1450 },
    { rank: 3, nickname: '교실탈출러', comment: '4번만에 성공!', score: 1420 },
    { rank: 4, nickname: '조심조심', comment: '심장이 쫄깃', score: 1380 },
    { rank: 5, nickname: '달리기선수', comment: '스릴 있어요', score: 1350 },
    { rank: 6, nickname: '눈치게임', comment: '집중력 게임', score: 1320 },
    { rank: 7, nickname: '스텔스마스터', comment: '재도전 ㄱㄱ', score: 1300 },
    { rank: 8, nickname: '교실여행', comment: '추억 돋네요', score: 1280 },
    { rank: 9, nickname: '무빙스톱', comment: '어렵다 ㅠㅠ', score: 1250 },
    { rank: 10, nickname: '참을인', comment: '인내심 테스트', score: 1230 },
    { rank: 11, nickname: '타이밍왕', comment: '타이밍이 중요', score: 1200 },
    { rank: 12, nickname: '달려라하니', comment: '또 도전!', score: 1180 },
    { rank: 13, nickname: '프리즈마스터', comment: '얼음땡!', score: 1150 },
    { rank: 14, nickname: '교실탈출', comment: '긴장감 최고', score: 1120 },
    { rank: 15, nickname: '무궁화꽃', comment: '피었습니다', score: 1100 },
    { rank: 16, nickname: '집중력왕', comment: '집중 필수', score: 1080 },
    { rank: 17, nickname: '스피드러너', comment: '속도가 생명', score: 1050 },
    { rank: 18, nickname: '냉정침착', comment: '침착하게', score: 1030 },
    { rank: 19, nickname: '게임마스터', comment: '꿀잼 ㅋㅋ', score: 1000 },
    { rank: 20, nickname: '도전자', comment: '계속 도전!', score: 980 },
    { rank: 21, nickname: '교실러', comment: '교실 최고', score: 950 },
    { rank: 22, nickname: '타이밍맨', comment: '타이밍 연습', score: 920 },
    { rank: 23, nickname: '무빙프리즈', comment: '멈춰!', score: 900 },
    { rank: 24, nickname: '스텔스', comment: '몰래몰래', score: 880 },
    { rank: 25, nickname: '달리기왕', comment: '달려!', score: 850 },
    { rank: 26, nickname: '교실탈주', comment: '탈출 성공', score: 820 },
    { rank: 27, nickname: '무궁화게임', comment: '추억의 게임', score: 800 },
    { rank: 28, nickname: '눈치왕', comment: '눈치 게임', score: 780 },
    { rank: 29, nickname: '빠른발', comment: '발빠르게', score: 750 },
    { rank: 30, nickname: '게임러버', comment: '게임 좋아', score: 720 },
  ],
  weekly: [
    { rank: 1, nickname: '주간1위', comment: '1주일 동안 열심히!', score: 1580 },
    { rank: 2, nickname: '주간왕', comment: '주간 랭킹 도전', score: 1550 },
    { rank: 3, nickname: '주말파이터', comment: '주말에 연습', score: 1520 },
    { rank: 4, nickname: '일주일왕', comment: '매일 플레이', score: 1490 },
    { rank: 5, nickname: '위클리킹', comment: '주간 달성', score: 1460 },
    { rank: 6, nickname: '7일천하', comment: '7일간 도전', score: 1430 },
    { rank: 7, nickname: '주간도전자', comment: '꾸준히', score: 1400 },
    { rank: 8, nickname: '위클리', comment: '주간 기록', score: 1370 },
    { rank: 9, nickname: '일주일챌린지', comment: '챌린지 성공', score: 1340 },
    { rank: 10, nickname: '주간랭커', comment: '랭킹 유지', score: 1310 },
    { rank: 11, nickname: '7일동안', comment: '매일매일', score: 1280 },
    { rank: 12, nickname: '주말게이머', comment: '주말 플레이', score: 1250 },
    { rank: 13, nickname: '위클리플레이어', comment: '주간 플레이', score: 1220 },
    { rank: 14, nickname: '일주일', comment: '일주일 완주', score: 1190 },
    { rank: 15, nickname: '주간게임', comment: '게임 재미', score: 1160 },
    { rank: 16, nickname: '위클리마스터', comment: '주간 마스터', score: 1130 },
    { rank: 17, nickname: '7일기록', comment: '기록 갱신', score: 1100 },
    { rank: 18, nickname: '주간도전', comment: '도전 계속', score: 1070 },
    { rank: 19, nickname: '일주일동안', comment: '열심히', score: 1040 },
    { rank: 20, nickname: '위클리러너', comment: '러너 등장', score: 1010 },
    { rank: 21, nickname: '주간파이터', comment: '파이팅', score: 980 },
    { rank: 22, nickname: '7일완주', comment: '완주 성공', score: 950 },
    { rank: 23, nickname: '주간게이머', comment: '게이머 인정', score: 920 },
    { rank: 24, nickname: '위클리스타', comment: '스타 등극', score: 890 },
    { rank: 25, nickname: '일주일마스터', comment: '마스터 달성', score: 860 },
    { rank: 26, nickname: '주간플레이', comment: '플레이 지속', score: 830 },
    { rank: 27, nickname: '7일챌린저', comment: '챌린저', score: 800 },
    { rank: 28, nickname: '주간러너', comment: '러너 활약', score: 770 },
    { rank: 29, nickname: '위클리챔프', comment: '챔프 도전', score: 740 },
    { rank: 30, nickname: '일주일게임', comment: '게임 즐김', score: 710 },
  ],
  monthly: [
    { rank: 1, nickname: '월간전설', comment: '한 달 최고 기록!', score: 1650 },
    { rank: 2, nickname: '먼슬리킹', comment: '월간 1위 도전', score: 1620 },
    { rank: 3, nickname: '30일왕', comment: '30일 달성', score: 1590 },
    { rank: 4, nickname: '월간마스터', comment: '마스터 등극', score: 1560 },
    { rank: 5, nickname: '먼슬리챔피언', comment: '챔피언!', score: 1530 },
    { rank: 6, nickname: '한달천하', comment: '한 달 동안', score: 1500 },
    { rank: 7, nickname: '월간도전자', comment: '매일 도전', score: 1470 },
    { rank: 8, nickname: '30일챌린지', comment: '챌린지 완료', score: 1440 },
    { rank: 9, nickname: '먼슬리프로', comment: '프로 등장', score: 1410 },
    { rank: 10, nickname: '월간랭커', comment: '랭커 유지', score: 1380 },
    { rank: 11, nickname: '한달동안', comment: '꾸준히', score: 1350 },
    { rank: 12, nickname: '30일완주', comment: '완주 성공', score: 1320 },
    { rank: 13, nickname: '월간게이머', comment: '게이머 인증', score: 1290 },
    { rank: 14, nickname: '먼슬리스타', comment: '스타 탄생', score: 1260 },
    { rank: 15, nickname: '한달게임', comment: '게임 마스터', score: 1230 },
    { rank: 16, nickname: '30일기록', comment: '기록 달성', score: 1200 },
    { rank: 17, nickname: '월간파이터', comment: '파이터 등장', score: 1170 },
    { rank: 18, nickname: '먼슬리러너', comment: '러너 활약', score: 1140 },
    { rank: 19, nickname: '한달마스터', comment: '마스터 인증', score: 1110 },
    { rank: 20, nickname: '30일도전', comment: '도전 성공', score: 1080 },
    { rank: 21, nickname: '월간플레이어', comment: '플레이어', score: 1050 },
    { rank: 22, nickname: '먼슬리히어로', comment: '히어로 등장', score: 1020 },
    { rank: 23, nickname: '한달동안게임', comment: '게임 열심히', score: 990 },
    { rank: 24, nickname: '30일플레이', comment: '플레이 지속', score: 960 },
    { rank: 25, nickname: '월간챌린저', comment: '챌린저', score: 930 },
    { rank: 26, nickname: '먼슬리게임', comment: '게임 재미', score: 900 },
    { rank: 27, nickname: '한달완주', comment: '완주 달성', score: 870 },
    { rank: 28, nickname: '30일마스터', comment: '마스터', score: 840 },
    { rank: 29, nickname: '월간러버', comment: '게임 러버', score: 810 },
    { rank: 30, nickname: '먼슬리팬', comment: '팬 인증', score: 780 },
  ],
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get a random duration between min and max (inclusive)
 */
export const getRandomDuration = (min: number, max: number): number => {
  return random(min, max, false);
};

/**
 * Calculate distance between two positions
 */
export const calculateDistance = (pos1: Position, pos2: Position): number => {
  const dx = pos1.x - pos2.x;
  const dy = pos1.y - pos2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Calculate final score based on base score and remaining time
 *
 * Formula: baseScore + (linear bonus) + (quadratic bonus)
 * - Linear bonus: remainingTime * timeMultiplier
 * - Quadratic bonus: (remainingTime / totalTime)^2 * timeBonusMultiplier
 *
 * Heavily weighted towards quadratic bonus to create steep score drops
 * as time decreases. No cap - perfect play gets perfect score.
 *
 * Score ranges (for typical game completion times):
 * - 60 seconds (perfect): 3000
 * - 50 seconds: 2250
 * - 40 seconds: 1633
 * - 35 seconds: 1408
 * - 30 seconds: 1200
 * - 25 seconds: 1008
 * - 20 seconds: 833
 * - 15 seconds: 675
 * - 10 seconds: 533
 * - 5 seconds: 408
 * - 0 seconds: 300
 */
export const calculateScore = (
  baseScore: number,
  remainingTime: number,
  timeMultiplier: number,
  totalTime: number = RED_LIGHT_GREEN_LIGHT_CONFIG.TOTAL_TIME,
  timeBonusMultiplier: number = RED_LIGHT_GREEN_LIGHT_CONFIG.TIME_BONUS_MULTIPLIER,
): number => {
  const linearBonus = remainingTime * timeMultiplier;
  const timeRatio = remainingTime / totalTime;
  const quadraticBonus = Math.floor(timeRatio * timeRatio * timeBonusMultiplier);

  return baseScore + linearBonus + quadraticBonus;
};

/**
 * Apply difficulty preset to base config
 */
export const applyDifficulty = (
  baseConfig: GameConfig,
  difficulty: DifficultyLevel,
): GameConfig => {
  return {
    ...baseConfig,
    ...DIFFICULTY_PRESETS[difficulty],
  };
};
